<template>
   <div>
      <div class="row p-2 mt-4">
         <div class="col-12">
            <span class="custom-span-lg" style="color: #435666;">Platform Guide
               <i class="fa-solid fa-arrow-right"></i>
            </span>
            <span class="custom-span-no-weight" style="color: #435666;"> &nbsp;User Guide</span>
            <div class="mt-2">
               <small class="text-muted">
                  <i class="fa-solid fa-graduation-cap me-1" style="color: #00A0C1;"></i>
                  Understand how XSurfLog policy evaluation engine, rules matching, and precedence hierarchies are enforced.
               </small>
            </div>
         </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="mt-4 mb-2">
         <ul class="nav nav-pills custom-tabs mb-4 p-1 bg-light rounded-pill" style="max-width: 480px; border: 1px solid #eef1f3;">
            <li class="nav-item flex-fill text-center">
               <button 
                  class="nav-link rounded-pill py-2 w-100 font-weight-600" 
                  :class="{ active: activeTab === 'guide' }"
                  @click="activeTab = 'guide'"
               >
                  <i class="fa-solid fa-book-open me-1 text-cyan"></i>
                  Written User Guide
               </button>
            </li>
            <li class="nav-item flex-fill text-center">
               <button 
                  class="nav-link rounded-pill py-2 w-100 font-weight-600" 
                  :class="{ active: activeTab === 'simulator' }"
                  @click="activeTab = 'simulator'"
               >
                  <i class="fa-solid fa-wand-magic-sparkles me-1 text-cyan"></i>
                  Interactive Simulator
               </button>
            </li>
         </ul>
      </div>

      <!-- TAB 1: INTERACTIVE SIMULATOR -->
      <div v-show="activeTab === 'simulator'" class="row g-4">
         
         <!-- Left Column: Interactive Simulator & Inputs -->
         <div class="col-lg-7">
            <div class="card h-100 shadow-sm border-0">
               <div class="card-header bg-white border-0 py-3">
                  <h5 class="m-0 font-weight-bold text-dark-blue">
                     <i class="fa-solid fa-wand-magic-sparkles me-2 text-cyan"></i>
                     Live Policy Simulator
                  </h5>
                  <p class="text-muted small mb-0 mt-1">Simulate how the Go policy backend evaluates your URL and rule configurations in real-time.</p>
               </div>
               
               <div class="card-body pt-0">
                  <!-- URL Input Group -->
                  <div class="mb-4">
                     <label class="form-label font-weight-600 text-secondary">Test a URL or IP Address</label>
                     <div class="input-group input-group-lg search-group">
                        <span class="input-group-text bg-light border-end-0 text-muted">
                           <i class="fa-solid fa-link"></i>
                        </span>
                        <input 
                           type="text" 
                           class="form-control bg-light border-start-0 ps-1" 
                           placeholder="e.g. https://google.com/search or 10.0.0.5" 
                           v-model="inputUrl"
                           @input="evaluatePolicy"
                        />
                        <button class="btn btn-outline-secondary border-start-0" type="button" @click="resetUrl" title="Clear URL">
                           <i class="fa-solid fa-xmark"></i>
                        </button>
                     </div>
                     
                     <!-- Quick Try Presets -->
                     <div class="mt-2 d-flex flex-wrap gap-2 align-items-center">
                        <span class="text-muted small">Quick Presets:</span>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('https://www.google.com/search', {})">Google Search</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('http://unsecured-site.com/login', { enableBlockHttp: true })">HTTP Login</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('https://chatgpt.com', {})">ChatGPT (AI Domain)</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('https://bad-tracker.info/malware', {})">Malicious Site</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('10.250.4.12', {})">Private CIDR IP</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('https://secure-paypal-login.info', { pageHasPassword: true })">Phishing Demo</button>
                        <button class="btn btn-xs btn-preset" @click="loadPreset('https://dev.xsurflog.com', {})">Forced Bypass</button>
                     </div>
                  </div>

                  <hr class="border-light my-4">

                  <!-- Configuration & Settings -->
                  <h6 class="font-weight-bold text-dark-blue mb-3">
                     <i class="fa-solid fa-sliders me-2 text-cyan"></i>
                     Simulated Account Settings
                  </h6>
                  
                  <div class="row g-3 mb-4">
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="blockAllSwitch" v-model="settings.blockAllWebsites" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="blockAllSwitch">
                              <strong>Block All Websites</strong>
                              <span class="d-block text-muted small">Denies everything except allowlist</span>
                           </label>
                        </div>
                     </div>
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="blockHttpSwitch" v-model="settings.enableBlockHttp" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="blockHttpSwitch">
                              <strong>Block HTTP</strong>
                              <span class="d-block text-muted small">Blocks non-HTTPS unencrypted connections</span>
                           </label>
                        </div>
                     </div>
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="blockChatSwitch" v-model="settings.blockChatMessaging" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="blockChatSwitch">
                              <strong>Block Chat &amp; Messaging</strong>
                              <span class="d-block text-muted small">Blocks Slack, Teams, ChatGPT</span>
                           </label>
                        </div>
                     </div>
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="blockFileSwitch" v-model="settings.blockFileSharing" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="blockFileSwitch">
                              <strong>Block File Sharing</strong>
                              <span class="d-block text-muted small">Blocks Dropbox, OneDrive, Box</span>
                           </label>
                        </div>
                     </div>
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="phishingProtectionSwitch" v-model="settings.isPhishingProtectionEnabled" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="phishingProtectionSwitch">
                              <strong>Phishing Protection</strong>
                              <span class="d-block text-muted small">Enables scan-based phishing blocking</span>
                           </label>
                        </div>
                     </div>
                     <div class="col-md-6">
                        <div class="form-check form-switch custom-switch-card">
                           <input class="form-check-input" type="checkbox" id="pageHasPasswordSwitch" v-model="settings.pageHasPassword" @change="evaluatePolicy">
                           <label class="form-check-label ms-2" for="pageHasPasswordSwitch">
                              <strong>Page contains Password Field</strong>
                              <span class="d-block text-muted small">Simulate entering password on scanned page</span>
                           </label>
                        </div>
                     </div>
                  </div>

                  <!-- Advanced Core Settings Selects -->
                  <div class="row g-3 mb-4">
                     <div class="col-md-6">
                        <label class="form-label small font-weight-bold text-secondary">Government TLDs Setting (Global)</label>
                        <select class="form-select bg-light border-0" v-model="settings.governmentTldSetting" @change="evaluatePolicy">
                           <option :value="0">Account Specific Rules</option>
                           <option :value="1">Master Allow All Gov TLDs</option>
                           <option :value="2">Master Block All Gov TLDs</option>
                        </select>
                     </div>
                     <div class="col-md-6">
                        <label class="form-label small font-weight-bold text-secondary">AI Categories Setting (Global)</label>
                        <select class="form-select bg-light border-0" v-model="settings.aiCategorySetting" @change="evaluatePolicy">
                           <option :value="0">Account Specific Rules</option>
                           <option :value="1">Master Allow All AI Domains</option>
                           <option :value="2">Master Block All AI Domains</option>
                        </select>
                     </div>
                  </div>

                  <hr class="border-light my-4">

                  <!-- Custom Rules Setup -->
                  <h6 class="font-weight-bold text-dark-blue mb-3">
                     <i class="fa-solid fa-list-check me-2 text-cyan"></i>
                     Custom Simulated Policy Lists
                  </h6>

                  <div class="accordion" id="rulesAccordion">
                     
                     <!-- Global Whitelist / Forced Whitelist -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#forcedCollapse">
                              <i class="fa-solid fa-shield-halved text-info me-2"></i>
                              Global Whitelist (Forced Whitelist)
                              <span class="badge bg-info bg-opacity-10 text-info rounded-pill ms-auto px-2 py-1 small">{{ whiteListDomains.length }} rules</span>
                           </button>
                        </h2>
                        <div id="forcedCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <p class="text-muted small">Global Whitelist (Forced Whitelist) rules are platform/administrator-enforced rules that bypass all standard blocking controls, custom blocklists, phishing scans, and content categories instantly.</p>
                              <div class="d-flex gap-2 mb-3">
                                 <input type="text" class="form-control form-control-sm border-0 shadow-sm" placeholder="Add forced rule e.g. *.vertexcybersecurity.com" v-model="newForcedRule" @keyup.enter="addRule('forced')">
                                 <button class="btn btn-sm btn-cyan" @click="addRule('forced')"><i class="fa-solid fa-plus"></i></button>
                              </div>
                              <div class="d-flex flex-wrap gap-2">
                                 <span class="badge badge-rule bg-white border d-flex align-items-center gap-1 text-dark" v-for="(rule, idx) in whiteListDomains" :key="'forced-'+idx">
                                    {{ rule }}
                                    <i class="fa-solid fa-xmark text-danger hand" @click="removeRule('forced', idx)"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Allow List -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#allowCollapse">
                              <i class="fa-solid fa-circle-check text-success me-2"></i>
                              Allow List (Whitelist) Rules
                              <span class="badge bg-success bg-opacity-10 text-success rounded-pill ms-auto px-2 py-1 small">{{ allowList.length }} rules</span>
                           </button>
                        </h2>
                        <div id="allowCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <p class="text-muted small">Requests matching allowlist rules bypass all blocks. Supports wildcards (*.domain.com, domain.*), CIDRs (10.0.0.0/8), and path suffixes (/path/*).</p>
                              <div class="d-flex gap-2 mb-3">
                                 <input type="text" class="form-control form-control-sm border-0 shadow-sm" placeholder="Add rule e.g. *.github.com" v-model="newAllowRule" @keyup.enter="addRule('allow')">
                                 <button class="btn btn-sm btn-cyan" @click="addRule('allow')"><i class="fa-solid fa-plus"></i></button>
                              </div>
                              <div class="d-flex flex-wrap gap-2">
                                 <span class="badge badge-rule bg-white border d-flex align-items-center gap-1 text-dark" v-for="(rule, idx) in allowList" :key="'allow-'+idx">
                                    {{ rule }}
                                    <i class="fa-solid fa-xmark text-danger hand" @click="removeRule('allow', idx)"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Block List -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#blockCollapse">
                              <i class="fa-solid fa-circle-xmark text-danger me-2"></i>
                              Block List Rules
                              <span class="badge bg-danger bg-opacity-10 text-danger rounded-pill ms-auto px-2 py-1 small">{{ blockList.length }} rules</span>
                           </button>
                        </h2>
                        <div id="blockCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <p class="text-muted small">Requests matching blocklist rules are immediately restricted (Code 315). Supports wildcards and CIDRs.</p>
                              <div class="d-flex gap-2 mb-3">
                                 <input type="text" class="form-control form-control-sm border-0 shadow-sm" placeholder="Add rule e.g. *.malware.info" v-model="newBlockRule" @keyup.enter="addRule('block')">
                                 <button class="btn btn-sm btn-cyan" @click="addRule('block')"><i class="fa-solid fa-plus"></i></button>
                              </div>
                              <div class="d-flex flex-wrap gap-2">
                                 <span class="badge badge-rule bg-white border d-flex align-items-center gap-1 text-dark" v-for="(rule, idx) in blockList" :key="'block-'+idx">
                                    {{ rule }}
                                    <i class="fa-solid fa-xmark text-danger hand" @click="removeRule('block', idx)"></i>
                                 </span>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Government TLDs (Platform Control Panel) -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#tldCategoryCollapse">
                              <i class="fa-solid fa-building text-success me-2"></i>
                              Government TLDs (Platform Control Panel)
                           </button>
                        </h2>
                        <div id="tldCategoryCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <div class="alert alert-info py-2" style="font-size: 11.5px; line-height: 1.4;" v-if="settings.governmentTldSetting !== 0">
                                 <i class="fa-solid fa-info-circle me-1"></i>
                                 <strong>Global Setting Override:</strong> Individual controls are locked because Government TLDs is set to 
                                 <em>{{ settings.governmentTldSetting === 1 ? 'Master Allow' : 'Master Block' }}</em>.
                              </div>
                              <p class="text-muted small">Manage access to government extensions. Individually configure <strong>Allow</strong> (1), <strong>Off/Default</strong> (0), or <strong>Block</strong> (2) for each TLD.</p>
                              
                              <div class="row g-2">
                                 <div class="col-12" v-for="tldItem in governmentTlds" :key="tldItem.Tld">
                                    <div class="d-flex align-items-center justify-content-between p-2 rounded border bg-white shadow-xs">
                                       <div>
                                          <strong class="small text-dark-blue">{{ tldItem.Name }}</strong>
                                          <span class="d-block text-muted font-monospace" style="font-size: 10px;">{{ tldItem.Tld }}</span>
                                       </div>
                                       
                                       <div class="btn-group btn-group-xs" role="group">
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-success btn-xs px-2 py-1" 
                                             :class="{ active: getTldStatus(tldItem) === 1 }"
                                             :disabled="settings.governmentTldSetting !== 0"
                                             @click="setTldStatus(tldItem, 1)"
                                          >
                                             Allow
                                          </button>
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-secondary btn-xs px-2 py-1" 
                                             :class="{ active: getTldStatus(tldItem) === 0 }"
                                             :disabled="settings.governmentTldSetting !== 0"
                                             @click="setTldStatus(tldItem, 0)"
                                          >
                                             Off
                                          </button>
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-danger btn-xs px-2 py-1" 
                                             :class="{ active: getTldStatus(tldItem) === 2 }"
                                             :disabled="settings.governmentTldSetting !== 0"
                                             @click="setTldStatus(tldItem, 2)"
                                          >
                                             Block
                                          </button>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- AI Categories (Platform Control Panel) -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#aiCategoryCollapse">
                              <i class="fa-solid fa-robot text-info me-2"></i>
                              AI Categories (Platform Control Panel)
                           </button>
                        </h2>
                        <div id="aiCategoryCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <div class="alert alert-info py-2" style="font-size: 11.5px; line-height: 1.4;" v-if="settings.aiCategorySetting !== 0">
                                 <i class="fa-solid fa-info-circle me-1"></i>
                                 <strong>Global Setting Override:</strong> Individual controls are locked because AI Categories is set to 
                                 <em>{{ settings.aiCategorySetting === 1 ? 'Master Allow' : 'Master Block' }}</em>.
                              </div>
                              <p class="text-muted small">Manage access to AI tools. Individually configure <strong>Allow</strong> (1), <strong>Off/Default</strong> (0), or <strong>Block</strong> (2) for each platform.</p>
                              
                              <div class="row g-2">
                                 <div class="col-12" v-for="aiCat in aiCategories" :key="aiCat.Domain">
                                    <div class="d-flex align-items-center justify-content-between p-2 rounded border bg-white shadow-xs">
                                       <div>
                                          <strong class="small text-dark-blue">{{ aiCat.Name }}</strong>
                                          <span class="d-block text-muted font-monospace" style="font-size: 10px;">{{ aiCat.Domain }}</span>
                                       </div>
                                       
                                       <div class="btn-group btn-group-xs" role="group">
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-success btn-xs px-2 py-1" 
                                             :class="{ active: getAiStatus(aiCat) === 1 }"
                                             :disabled="settings.aiCategorySetting !== 0"
                                             @click="setAiStatus(aiCat, 1)"
                                          >
                                             Allow
                                          </button>
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-secondary btn-xs px-2 py-1" 
                                             :class="{ active: getAiStatus(aiCat) === 0 }"
                                             :disabled="settings.aiCategorySetting !== 0"
                                             @click="setAiStatus(aiCat, 0)"
                                          >
                                             Off
                                          </button>
                                          <button 
                                             type="button" 
                                             class="btn btn-outline-danger btn-xs px-2 py-1" 
                                             :class="{ active: getAiStatus(aiCat) === 2 }"
                                             :disabled="settings.aiCategorySetting !== 0"
                                             @click="setAiStatus(aiCat, 2)"
                                          >
                                             Block
                                          </button>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     <!-- Category Block Rules -->
                     <div class="accordion-item rule-accordion-item">
                        <h2 class="accordion-header">
                           <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#categoryCollapse">
                              <i class="fa-solid fa-tags text-warning me-2"></i>
                              Category Block Rules
                           </button>
                        </h2>
                        <div id="categoryCollapse" class="accordion-collapse collapse" data-bs-parent="#rulesAccordion">
                           <div class="accordion-body bg-light-gray">
                              <p class="text-muted small">Standard category filters evaluated via local cache at the end of the pipeline hierarchy.</p>
                              <div class="row g-2">
                                 <div class="col-md-6" v-for="(blocked, catName) in categories" :key="catName">
                                    <div class="form-check bg-white p-2 rounded border border-light ps-4">
                                       <input class="form-check-input" type="checkbox" :id="'cat-'+catName" v-model="categories[catName]" @change="evaluatePolicy">
                                       <label class="form-check-label ms-1 d-flex align-items-center justify-content-between w-100 pe-2" :for="'cat-'+catName">
                                          <span class="small font-weight-600">{{ catName }}</span>
                                          <span class="badge bg-danger bg-opacity-10 text-danger rounded-pill px-2 py-0.5" style="font-size: 9px;" v-if="blocked">Blocked</span>
                                          <span class="badge bg-success bg-opacity-10 text-success rounded-pill px-2 py-0.5" style="font-size: 9px;" v-else>Allowed</span>
                                       </label>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                  </div>
               </div>
            </div>
         </div>

         <!-- Right Column: Live Verdict & Evaluation Timeline -->
         <div class="col-lg-5">
            <div class="card h-100 shadow-sm border-0 bg-white">
               
               <!-- Big Verdict Header -->
               <div class="verdict-banner text-center p-4 d-flex flex-column align-items-center justify-content-center border-bottom border-light" :class="verdictClass">
                  <div class="verdict-icon-container mb-2">
                     <i :class="verdictIcon"></i>
                  </div>
                  <h3 class="m-0 font-weight-bold text-uppercase tracking-wider">{{ verdict.result }}</h3>
                  <p class="m-0 mt-1 small opacity-90 font-weight-500">{{ verdict.reason }}</p>
                  
                  <div class="verdict-meta-badges mt-3 d-flex gap-2" v-if="verdict.result === 'Blocked'">
                     <span class="badge bg-black bg-opacity-25 rounded-pill px-3 py-1 font-monospace">Unsafe Code: {{ verdict.code }}</span>
                     <span class="badge bg-black bg-opacity-25 rounded-pill px-3 py-1">Type: {{ verdict.blockType }}</span>
                  </div>
                  <div class="verdict-meta-badges mt-3" v-else>
                     <span class="badge bg-white bg-opacity-25 rounded-pill px-3 py-1 text-white">Status: Enforced &amp; Logged</span>
                  </div>
               </div>

               <!-- Extracted URL Parameters (Quick Insight) -->
               <div class="bg-light p-3 border-bottom border-light">
                  <h6 class="text-secondary small font-weight-700 uppercase mb-2">Extracted URL Parameters</h6>
                  <div class="row g-2 text-dark">
                     <div class="col-6">
                        <div class="small bg-white p-2 rounded border border-light">
                           <span class="text-muted d-block small" style="font-size: 10px;">DOMAIN / HOST</span>
                           <strong class="font-monospace text-dark-blue">{{ parsed.host || 'N/A' }}</strong>
                        </div>
                     </div>
                     <div class="col-6">
                        <div class="small bg-white p-2 rounded border border-light">
                           <span class="text-muted d-block small" style="font-size: 10px;">SCHEME</span>
                           <strong class="font-monospace text-dark-blue">{{ parsed.scheme || 'N/A' }}</strong>
                        </div>
                     </div>
                     <div class="col-6">
                        <div class="small bg-white p-2 rounded border border-light">
                           <span class="text-muted d-block small" style="font-size: 10px;">URL PATH</span>
                           <strong class="font-monospace text-dark-blue">{{ parsed.path || '/' }}</strong>
                        </div>
                     </div>
                     <div class="col-6">
                        <div class="small bg-white p-2 rounded border border-light">
                           <span class="text-muted d-block small" style="font-size: 10px;">IP / CIDR TYPE</span>
                           <strong class="font-monospace text-dark-blue">{{ parsed.isIp ? 'IPv4 / IPv6' : 'Standard Hostname' }}</strong>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Evaluation Pipeline Steps -->
               <div class="card-body p-4 overflow-auto scroll-container" style="max-height: 480px;">
                  <h6 class="font-weight-bold text-dark-blue mb-3">
                     <i class="fa-solid fa-route me-2 text-cyan"></i>
                     Evaluation Hierarchy Pipeline
                  </h6>

                  <div class="timeline">
                     <div class="timeline-item" v-for="(step, idx) in pipeline" :key="'step-'+idx" :class="stepClass(step.status)">
                        <div class="timeline-marker">
                           <i :class="stepIcon(step.status)"></i>
                        </div>
                        <div class="timeline-content pb-3 ms-3">
                           <div class="d-flex align-items-center justify-content-between">
                              <h6 class="timeline-title m-0 font-weight-700 text-dark-blue" style="font-size: 14px;">{{ step.name }}</h6>
                              <span class="badge rounded-pill small py-0.5 px-2 font-weight-600" :class="statusBadgeClass(step.status)">
                                 {{ statusLabel(step.status) }}
                              </span>
                           </div>
                           <p class="text-muted small m-0 mt-1 line-height-1.4">{{ step.message }}</p>
                        </div>
                     </div>
                  </div>
               </div>

            </div>
         </div>

      </div>

      <!-- TAB 2: WRITTEN USER GUIDE -->
      <div v-show="activeTab === 'guide'" class="row g-4">
         <div class="col-12">
            <div class="card border-0 shadow-sm p-4">
               
               <div class="border-bottom pb-3 mb-4">
                  <h4 class="font-weight-bold text-dark-blue"><i class="fa-solid fa-book-open text-cyan me-2"></i> policy engine reference guide</h4>
                  <p class="text-muted mb-0">Deep dive into XSurfLog's unified matching algorithms, rule formatting, and blocking pipeline.</p>
               </div>

               <!-- Core Overview Section -->
               <div class="mb-4">
                  <h5 class="text-dark-blue font-weight-bold">How Policy Enforcements Work</h5>
                  <p class="text-secondary line-height-1.7" style="font-size: 14.5px;">
                     The XSurfLog core policy engine evaluates web traffic in real-time. When a user navigates to a website, the XSurfLog browser extension and DNS gateway split the address into precise components: **Protocol Scheme**, **Target Host**, and **URI Path**. The engine then evaluates the address sequentially through our strict <strong>Precedence Hierarchy</strong>. As soon as a match triggers a final verdict, evaluation stops instantly.
                  </p>
               </div>

               <div class="alert alert-info d-flex align-items-start py-3 mb-5" style="border-radius: 10px;">
                  <i class="fa-solid fa-circle-info text-cyan me-3 mt-1" style="font-size: 1.25rem;"></i>
                  <div>
                     <strong class="text-dark-blue">The "Whitelist Override" Principal:</strong>
                     <p class="mb-0 mt-1 text-secondary small" style="line-height: 1.6;">
                        Allow List (Whitelist) patterns hold the highest evaluation priority. A matched whitelist pattern immediately bypasses standard threat database blocks, category blocks, AI category blocks, and customized blocklist filters, guaranteeing zero disruption for critical corporate resources.
                     </p>
                  </div>
               </div>

               <!-- Precedence Pipeline Timeline (Static Explanation) -->
               <h5 class="text-dark-blue font-weight-bold mb-3">Precedence Hierarchy Sequence</h5>
               <p class="text-muted small mb-4">The following sequence is executed strictly from top-to-bottom for every request. If a match occurs at an earlier stage, all subsequent checks are completely skipped.</p>

               <div class="static-steps-list mb-5">
                  <div class="step-card-wrapper p-3 mb-3 border rounded border-light bg-light-gray" v-for="(stepExplain, sIdx) in staticSteps" :key="'explain-'+sIdx">
                     <div class="d-flex align-items-start">
                        <span class="explain-num me-3">{{ sIdx + 1 }}</span>
                        <div class="flex-grow-1">
                           <div class="d-flex align-items-center justify-content-between">
                              <h6 class="font-weight-bold text-dark-blue m-0">{{ stepExplain.title }}</h6>
                              <span class="badge font-monospace bg-secondary bg-opacity-10 text-secondary" style="font-size: 11px;" v-if="stepExplain.code">Unsafe Code: {{ stepExplain.code }}</span>
                           </div>
                           <p class="text-secondary small m-0 mt-2 line-height-1.6">{{ stepExplain.description }}</p>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Patterns Table -->
               <h5 class="text-dark-blue font-weight-bold mb-3">Custom Rules Format Reference</h5>
               <p class="text-secondary small mb-4">Administrators can configure extremely powerful, unified wildcards and IP ranges inside XSurfLog lists. The syntax is supported identically across both Allow Lists and Block Lists.</p>

               <div class="table-responsive">
                  <table class="table table-bordered border-light align-middle text-secondary" style="font-size: 13.5px;">
                     <thead class="bg-light text-dark-blue font-weight-700">
                        <tr>
                           <th style="width: 25%;">Rule Syntax Pattern</th>
                           <th style="width: 30%;">Evaluation Mechanics</th>
                           <th style="width: 45%;">Matching Examples</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">domain.com</strong></td>
                           <td>Matches exact domain or any subdomains.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">domain.com</code>, <code class="text-dark">subdomain.domain.com</code>, <code class="text-dark">sub.domain.com</code><br>
                              <span class="badge bg-danger bg-opacity-10 text-danger me-1 mt-1">No Match</span> <code class="text-dark">otherdomain.com</code>
                           </td>
                        </tr>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">sub.domain.com</strong></td>
                           <td>Matches that exact subdomain and any of its nested child subdomains. Does NOT match the parent domain or sister subdomains.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">sub.domain.com</code>, <code class="text-dark">deep.sub.domain.com</code><br>
                              <span class="badge bg-danger bg-opacity-10 text-danger me-1 mt-1">No Match</span> <code class="text-dark">domain.com</code>, <code class="text-dark">othersub.domain.com</code>
                           </td>
                        </tr>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">*.domain.com</strong></td>
                           <td>Subdomain Wildcard. Matches any subdomain level, including root.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">domain.com</code>, <code class="text-dark">subdomain.domain.com</code>, <code class="text-dark">docs.domain.com</code>, <code class="text-dark">sub.docs.domain.com</code><br>
                              <span class="badge bg-danger bg-opacity-10 text-danger me-1 mt-1">No Match</span> <code class="text-dark">notdomain.com</code>
                           </td>
                        </tr>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">domain.*</strong></td>
                           <td>TLD Wildcard. Matches any TLD extension.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">domain.com</code>, <code class="text-dark">domain.org</code>, <code class="text-dark">domain.co.uk</code>, <code class="text-dark">domain.com.au</code>
                           </td>
                        </tr>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">domain.com/path/*</strong></td>
                           <td>Path Suffix. Forces domain matching AND path prefix matching.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">domain.com/path</code>, <code class="text-dark">domain.com/path/resource</code>, <code class="text-dark">domain.com/path/sub/file</code><br>
                              <span class="badge bg-danger bg-opacity-10 text-danger me-1 mt-1">No Match</span> <code class="text-dark">domain.com/other-path</code>
                           </td>
                        </tr>
                        <tr>
                           <td><strong class="font-monospace text-dark-blue">10.0.0.0/8</strong></td>
                           <td>IP CIDR Block. Matches any resolved or requested IP in network scope.</td>
                           <td>
                              <span class="badge bg-success bg-opacity-10 text-success me-1">Match</span> <code class="text-dark">10.15.2.4</code>, <code class="text-dark">10.250.40.12</code>, <code class="text-dark">10.0.0.1</code><br>
                              <span class="badge bg-danger bg-opacity-10 text-danger me-1 mt-1">No Match</span> <code class="text-dark">192.168.1.1</code>
                           </td>
                        </tr>
                     </tbody>
                  </table>
               </div>

            </div>
         </div>
      </div>

   </div>
</template>

<script>
export default {
   name: 'UserGuide',
   
   data() {
      return {
         activeTab: 'guide',
         inputUrl: 'https://www.google.com/search',
         newAllowRule: '',
         newBlockRule: '',
         newForcedRule: '',
         
         // Simulated Settings (Mirrors database AccountSettings columns)
         settings: {
            blockAllWebsites: false,
            enableBlockHttp: false,
            blockChatMessaging: false,
            blockFileSharing: false,
            isPhishingProtectionEnabled: true,
            pageHasPassword: false,
            governmentTldSetting: 0, // 0: Account Specific, 1: Master Allow, 2: Master Block
            aiCategorySetting: 0, // 0: Account Specific, 1: Master Allow, 2: Master Block
         },
         
         // Simulated Playlists
         whiteListDomains: ['*.vertexcybersecurity.com', 'microsoft.com'],
         allowList: ['*.google.com', '10.0.0.0/8', 'github.com/my-org/*', 'localhost'],
         blockList: ['*.malicious.info', '192.168.1.0/24', 'bad-site.*'],
         
         // Custom individual TLD parameters
         governmentTlds: [
            { Name: 'Australian Gov', Tld: '.gov.au', Description: 'Australian Government services', Status: 0 }, // Off/Default
            { Name: 'US Gov TLD', Tld: '.gov', Description: 'United States Federal Government', Status: 2 }, // Block
            { Name: 'UK Gov TLD', Tld: '.gov.uk', Description: 'United Kingdom Government', Status: 0 }, // Off/Default
            { Name: 'Singapore Gov TLD', Tld: '.gov.sg', Description: 'Singapore Government Registry', Status: 1 } // Allow
         ],
         
         // Real XSurfLog split categories
         aiCategories: [
            { Name: 'ChatGPT', Domain: 'chatgpt.com', Description: 'OpenAI main web portal', Status: 2 }, // Block
            { Name: 'OpenAI API', Domain: 'openai.com', Description: 'Developer platform and APIs', Status: 0 }, // Off (Default)
            { Name: 'Claude AI', Domain: 'claude.ai', Description: 'Anthropic LLM chat', Status: 2 }, // Block
            { Name: 'Gemini AI', Domain: 'gemini.google.com', Description: 'Google assistant & models', Status: 1 }, // Allow
            { Name: 'Cohere AI', Domain: 'cohere.com', Description: 'Enterprise text models', Status: 0 } // Off
         ],
         
         categories: {
            'Social Media': true,
            'Gambling': true,
            'Adult Content': true,
            'News & Media': false,
            'Shopping': false,
            'Business & Corporate': false
         },
         
         // Evaluation Results
         parsed: {
            host: '',
            path: '',
            scheme: '',
            isIp: false
         },
         verdict: {
            result: 'Allowed',
            reason: '',
            code: 0,
            blockType: ''
         },
         pipeline: [],

         // Static explanatory content for User Guide Tab 2
         staticSteps: [
            {
               title: "Global Whitelist / Account Allow List Bypass",
               code: null,
               description: "Initial bypass evaluation. If the requested URL or CIDR matches an entry in your account allowlist, all subsequent blocking rules are skipped entirely, and traffic is immediately permitted."
            },
            {
               title: "Block All Websites Setting",
               code: 357,
               description: "Global lockdowns setting. When active, blocks all domain requests unless explicitly whitelisted in step 1."
            },
            {
               title: "Threat Intelligence & Active Malware DB Check",
               code: 356,
               description: "Matches target hostnames against automated global block lists containing millions of verified phishing, adware, ransomware, and malware hosts."
            },
            {
               title: "Domain Length & Safety Heuristics Check",
               code: 353,
               description: "Bypasses URL evaluation and flags domains longer than 100 characters. These are highly likely to contain automated domain-generation algorithms (DGAs)."
            },
            {
               title: "Suspicious .info TLD Dashes Heuristic",
               code: 358,
               description: "Specialized heuristic checking for hyphens inside .info domains, which is highly characteristic of automated malware-delivery domains."
            },
            {
               title: "OAuth Connection Protection Lock",
               code: 360,
               description: "Restricts access to unauthorized corporate Single Sign-On (SSO) login portals (Google, Microsoft) to block external credentials leaks."
            },
            {
               title: "HTTP Protocol Enforcement (Block Unencrypted HTTP)",
               code: 346,
               description: "Enforces unencrypted standard HTTP traffic blockage. Bypasses on localhost to allow development, but redirects standard domains to secure HTTPS."
            },
            {
               title: "Specialized Advanced Protections (File Sharing & Chat)",
               code: "351 & 354",
               description: "Advanced plans filter. Protects your network against data leaks and unmonitored communication by blocking platforms like Dropbox (Code 351) or Slack/Teams (Code 354)."
            },
            {
               title: "Government TLDs Enforcements",
               code: 361,
               description: "Enforces global restrictions on government domains (.gov, .gov.au). Configurable as Master Block, Master Allow, or account-specific policies."
            },
            {
               title: "AI Categories Enforcements",
               code: 362,
               description: "Enforces policies on deep learning and conversational AI systems. Configurable as Master Block, Master Allow, or individual platform toggles."
            },
            {
               title: "Custom Block List Check",
               code: 315,
               description: "Evaluates standard patterns inside your custom organisational block lists. Supports exact, wildcard, CIDR, and path checks."
            },
            {
               title: "Category Block Rules",
               code: "349 & 355",
               description: "Final step. Queries local classifications cache. Blocks domains matching restricted content categories (Gambling, Social Media, Adult Content, etc.)."
            }
         ]
      }
   },
   
   computed: {
      verdictClass() {
         if (this.verdict.result === 'Allowed') return 'bg-success text-white';
         return 'bg-danger text-white';
      },
      verdictIcon() {
         if (this.verdict.result === 'Allowed') return 'fa-solid fa-circle-check verdict-large-icon';
         return 'fa-solid fa-circle-exclamation verdict-large-icon';
      }
   },
   
   mounted() {
      this.evaluatePolicy();
   },
   
   methods: {
      resetUrl() {
         this.inputUrl = '';
         this.evaluatePolicy();
      },
      
      loadPreset(url, customSettings) {
         this.inputUrl = url;
         // Reset settings, then apply custom
         this.settings.blockAllWebsites = false;
         this.settings.enableBlockHttp = false;
         this.settings.blockChatMessaging = false;
         this.settings.blockFileSharing = false;
         this.settings.isPhishingProtectionEnabled = true;
         this.settings.pageHasPassword = false;
         this.settings.governmentTldSetting = 0;
         this.settings.aiCategorySetting = 0;
         
         for (let key in customSettings) {
            this.settings[key] = customSettings[key];
         }
         
         this.evaluatePolicy();
      },
      
      addRule(type) {
         if (type === 'forced') {
            const val = this.newForcedRule.trim();
            if (val && !this.whiteListDomains.includes(val)) {
               this.whiteListDomains.push(val);
               this.newForcedRule = '';
            }
         } else if (type === 'allow') {
            const val = this.newAllowRule.trim();
            if (val && !this.allowList.includes(val)) {
               this.allowList.push(val);
               this.newAllowRule = '';
            }
         } else if (type === 'block') {
            const val = this.newBlockRule.trim();
            if (val && !this.blockList.includes(val)) {
               this.blockList.push(val);
               this.newBlockRule = '';
            }
         }
         this.evaluatePolicy();
      },
      
      removeRule(type, idx) {
         if (type === 'forced') {
            this.whiteListDomains.splice(idx, 1);
         } else if (type === 'allow') {
            this.allowList.splice(idx, 1);
         } else if (type === 'block') {
            this.blockList.splice(idx, 1);
         }
         this.evaluatePolicy();
      },

      getAiStatus(aiCat) {
         return aiCat.Status;
      },

      setAiStatus(aiCat, status) {
         aiCat.Status = status;
         this.evaluatePolicy();
      },

      getTldStatus(tldItem) {
         return tldItem.Status;
      },

      setTldStatus(tldItem, status) {
         tldItem.Status = status;
         this.evaluatePolicy();
      },
      
      // Core parser
      parseUrl(input) {
         let urlStr = input.trim();
         if (!urlStr) {
            return { host: '', path: '', scheme: '', isIp: false };
         }
         
         // If input looks like pure IP or domain, prepend scheme
         if (!/^https?:\/\//i.test(urlStr)) {
            urlStr = 'https://' + urlStr;
         }
         
         try {
            const urlObj = new URL(urlStr);
            let host = urlObj.hostname;
            let path = urlObj.pathname;
            let scheme = urlObj.protocol.replace(':', '');
            
            // Check if hostname is an IP address (IPv4 or IPv6)
            const isIp = /^[0-9.]+$/.test(host) || host.includes(':');
            
            return { host, path, scheme, isIp };
         } catch (e) {
            // fallback
            return { host: input, path: '', scheme: 'https', isIp: false };
         }
      },
      
      // CIDR Matcher
      matchCidr(ip, cidr) {
         try {
            const [range, bitsStr] = cidr.split('/');
            const bits = parseInt(bitsStr, 10);
            if (isNaN(bits)) return ip === range;
            
            const ipParts = ip.split('.').map(Number);
            const rangeParts = range.split('.').map(Number);
            if (ipParts.length !== 4 || rangeParts.length !== 4) return false;
            
            const ipNum = (ipParts[0] << 24) + (ipParts[1] << 16) + (ipParts[2] << 8) + ipParts[3];
            const rangeNum = (rangeParts[0] << 24) + (rangeParts[1] << 16) + (rangeParts[2] << 8) + rangeParts[3];
            
            // Generate mask
            const mask = ~( (1 << (32 - bits)) - 1 );
            return (ipNum & mask) === (rangeNum & mask);
         } catch (e) {
            return false;
         }
      },
      
      // Unified match helper (direct translation of Go backend logic)
      unifiedMatch(targetHost, targetPath, pattern, isIp) {
         if (!pattern || pattern === '*') return false;
         
         // Construct full target "host/path"
         let target = targetHost.toLowerCase().trim();
         if (targetPath) {
            let cleanPath = targetPath.toLowerCase().trim();
            // Ensure path starts with slash if target doesn't end with one and path doesn't start with one
            if (!cleanPath.startsWith('/')) {
               cleanPath = '/' + cleanPath;
            }
            target += cleanPath;
         }
         
         pattern = pattern.toLowerCase().trim();
         
         // Trim prefix slash if any
         if (target.startsWith('/')) target = target.substring(1);
         if (pattern.startsWith('/')) pattern = pattern.substring(1);
         
         // Split host and path in pattern
         let patternHost = pattern;
         let patternPath = '';
         const pSlashIdx = pattern.indexOf('/');
         if (pSlashIdx !== -1) {
            patternHost = pattern.substring(0, pSlashIdx);
            patternPath = pattern.substring(pSlashIdx);
         }
         
         // Split host and path in target
         let splitTargetHost = target;
         let splitTargetPath = '';
         const tSlashIdx = target.indexOf('/');
         if (tSlashIdx !== -1) {
            splitTargetHost = target.substring(0, tSlashIdx);
            splitTargetPath = target.substring(tSlashIdx);
         }
         
         // 1. Match Host
         let hostMatched = false;
         
         // CIDR match
         if (patternHost.includes('/') && isIp) {
            hostMatched = this.matchCidr(splitTargetHost, patternHost);
         } else if (patternHost.startsWith('*.')) {
            let suffix = patternHost.substring(1); // .domain.com
            let base = patternHost.substring(2); // domain.com
            hostMatched = (splitTargetHost !== base && splitTargetHost.endsWith(suffix));
         } else if (patternHost.endsWith('.*')) {
            let prefix = patternHost.substring(0, patternHost.length - 1);
            hostMatched = splitTargetHost.startsWith(prefix);
         } else {
            // Strip port
            const cleanTargetHost = splitTargetHost.split(':')[0];
            const cleanPatternHost = patternHost.split(':')[0];
            hostMatched = (cleanTargetHost === cleanPatternHost || cleanTargetHost.endsWith('.' + cleanPatternHost));
         }
         
         if (!hostMatched) return false;
         
         // 2. Match Path
         if (!patternPath || patternPath === '/*' || patternPath === '/') {
            return true;
         }
         
         // matchPath Go equivalent
         if (patternPath.endsWith('/*')) {
            let prefix = patternPath.substring(0, patternPath.length - 2);
            if (!prefix) return true;
            return splitTargetPath === prefix || splitTargetPath.startsWith(prefix + '/');
         }
         
         return splitTargetPath === patternPath || splitTargetPath === (patternPath + '/') || (splitTargetPath + '/') === patternPath;
      },

      // Specificity Scoring Engine (exact replicate of Go evaluateMatchGo)
      evaluateMatch(targetHost, targetPath, pattern, isIp) {
         if (!pattern || pattern === '*') {
            return { matched: false, score: 0 };
         }
         
         // Normalize pattern
         let normPattern = pattern.toLowerCase().trim();
         if (normPattern.startsWith('https://')) {
            normPattern = normPattern.substring(8);
         } else if (normPattern.startsWith('http://')) {
            normPattern = normPattern.substring(7);
         }
         if (normPattern.startsWith('www.')) {
            normPattern = normPattern.substring(4);
         }
         if (normPattern.startsWith('/')) {
            normPattern = normPattern.substring(1);
         }
         
         // Split pattern into host and path
         let patternHost = normPattern;
         let patternPath = '';
         const slashIdx = normPattern.indexOf('/');
         if (slashIdx !== -1) {
            patternHost = normPattern.substring(0, slashIdx);
            patternPath = normPattern.substring(slashIdx);
         }
         
         // 1. Evaluate Host Match
         let hostMatched = false;
         let hostScore = 0;
         
         if (patternHost.startsWith('*.')) {
            // Explicit subdomain wildcard
            const suffix = patternHost.substring(1); // ".blender.org"
            const base = patternHost.substring(2); // "blender.org"
            if (targetHost !== base && targetHost.endsWith(suffix)) {
               hostMatched = true;
               hostScore = 500;
            }
         } else if (patternHost.endsWith('.*')) {
            // TLD wildcard
            const prefix = patternHost.substring(0, patternHost.length - 1);
            if (targetHost.startsWith(prefix)) {
               hostMatched = true;
               hostScore = 500;
            }
         } else if (patternHost.includes('/') && isIp) {
            // CIDR Match
            if (this.matchCidr(targetHost, patternHost)) {
               hostMatched = true;
               hostScore = 500;
            }
         } else {
            // Exact or Implicit Match
            const patternHostStripped = patternHost.split(':')[0]; // strip port
            if (targetHost === patternHostStripped) {
               hostMatched = true;
               hostScore = 1000;
            } else if (targetHost.endsWith('.' + patternHostStripped)) {
               hostMatched = true;
               hostScore = 0;
            }
         }
         
         if (!hostMatched) return { matched: false, score: 0 };
         
         // 2. Evaluate Path Match
         let pathMatched = false;
         let pathScore = 0;
         
         if (!patternPath || patternPath === '/*' || patternPath === '/') {
            pathMatched = true;
            pathScore = 0;
         } else if (patternPath.endsWith('/*')) {
            const prefix = patternPath.substring(0, patternPath.length - 2);
            if (!prefix || targetPath === prefix || targetPath.startsWith(prefix + '/')) {
               pathMatched = true;
               pathScore = 500;
            }
         } else {
            if (targetPath === patternPath || targetPath === patternPath + '/' || (targetPath + '/') === patternPath) {
               pathMatched = true;
               pathScore = 1000;
            }
         }
         
         if (!pathMatched) return { matched: false, score: 0 };
         
         // 3. Compute base specificity (length of cleaned pattern)
         let cleanPattern = normPattern;
         if (cleanPattern.startsWith('*.')) {
            cleanPattern = cleanPattern.substring(2);
         } else if (cleanPattern.startsWith('*')) {
            cleanPattern = cleanPattern.substring(1);
         }
         
         if (cleanPattern.endsWith('/*')) {
            cleanPattern = cleanPattern.substring(0, cleanPattern.length - 2);
         } else if (cleanPattern.endsWith('.*')) {
            cleanPattern = cleanPattern.substring(0, cleanPattern.length - 2);
         } else if (cleanPattern.endsWith('*')) {
            cleanPattern = cleanPattern.substring(0, cleanPattern.length - 1);
         }
         
         const baseSpecificity = cleanPattern.length;
         const totalScore = baseSpecificity + hostScore + pathScore;
         
         return { matched: true, score: totalScore };
      },
      
      // Core simulation pipeline
      evaluatePolicy() {
         const urlStr = this.inputUrl.trim();
         if (!urlStr) {
            this.parsed = { host: '', path: '', scheme: '', isIp: false };
            this.verdict = { result: 'Allowed', reason: 'No input to evaluate.', code: 0, blockType: '' };
            this.pipeline = [];
            return;
         }
         
         const parsed = this.parseUrl(urlStr);
         this.parsed = parsed;
         
         const pipeline = [];
         let finalVerdict = { result: 'Allowed', reason: 'URL evaluated successfully against security policies.', code: 0, blockType: '' };
         let isTerminated = false;
         let isWhitelisted = false;
         let matchedAllowRule = '';
         
         // ── STEP 1: URL Normalization & Parsing ──
         pipeline.push({
            name: 'URL Normalization & Parsing',
            status: 'EVALUATED_PASS',
            message: `Extracted domain: "${parsed.host}", scheme: "${parsed.scheme}", and path: "${parsed.path || '/'}". IP format: ${parsed.isIp ? 'Yes' : 'No'}.`
         });
         
         // ── STEP 2: Hardcoded System Whitelist Check ──
         const systemDomains = [
            "vertexcybersecurity.com",
            "vertexcybersecurity.com.au",
            "vertexsecurity.com.au",
            "vtxsecurity.com",
            "vtxsecurity.com.au",
            "xsurflog.com",
            "bad4business.com.au",
            "npvertexsecurity.com.au",
            "test.xsurflog.com",
            "uat.xsurflog.com",
            "dev.xsurflog.com",
            "new.xsurflog.com"
         ];
         let matchedSystemDomain = '';
         const lowerHost = parsed.host.toLowerCase();
         if (lowerHost === 'localhost' || lowerHost === '127.0.0.1') {
            matchedSystemDomain = lowerHost;
         } else {
            for (const fallback of systemDomains) {
               if (lowerHost === fallback || lowerHost.endsWith("." + fallback)) {
                  matchedSystemDomain = fallback;
                  break;
               }
            }
         }
         
         if (matchedSystemDomain) {
            isWhitelisted = true;
            matchedAllowRule = `System Whitelist (${matchedSystemDomain})`;
            pipeline.push({
               name: 'System Whitelist Bypass',
               status: 'EVALUATED_PASS_BYPASS',
               message: `Matched hardcoded system domain "${matchedSystemDomain}". All subsequent policy checks bypassed instantly.`
            });
         } else {
            pipeline.push({
               name: 'System Whitelist Bypass',
               status: 'EVALUATED_SKIP',
               message: 'Did not match any system domains. Proceeding.'
            });
         }
         
         // ── STEP 3: Forced Whitelist / Global Whitelist (whiteListDomains) Check ──
         let matchedForcedRule = '';
         if (!isWhitelisted) {
            for (let rule of this.whiteListDomains) {
               if (this.unifiedMatch(parsed.host, parsed.path, rule, parsed.isIp)) {
                  isWhitelisted = true;
                  matchedAllowRule = `Global Whitelist (${rule})`;
                  matchedForcedRule = rule;
                  break;
               }
            }
         }
         
         if (matchedForcedRule) {
            pipeline.push({
               name: 'Global Whitelist (Forced Whitelist)',
               status: 'EVALUATED_PASS_BYPASS',
               message: `Matched Global/Forced Whitelist rule "${matchedForcedRule}". Bypass activated instantly for administrative override.`
            });
         } else {
            pipeline.push({
               name: 'Global Whitelist (Forced Whitelist)',
               status: isWhitelisted ? 'BYPASSED' : 'EVALUATED_SKIP',
               message: isWhitelisted ? 'Skipped: Already whitelisted by higher-priority step.' : 'Did not match Global Whitelist administrative overrides.'
            });
         }
         
         // ── STEP 4: Specificity-Based Custom Rules Engine (Allow List vs Block List) ──
         let maxAllowSpecificity = -1;
         let matchedCustomAllowRule = null;
         let maxBlockSpecificity = -1;
         let matchedCustomBlockRule = null;
         
         if (!isWhitelisted) {
            // Evaluate custom Allow List
            for (let rule of this.allowList) {
               const matchRes = this.evaluateMatch(parsed.host, parsed.path, rule, parsed.isIp);
               if (matchRes.matched) {
                  if (matchRes.score > maxAllowSpecificity) {
                     maxAllowSpecificity = matchRes.score;
                     matchedCustomAllowRule = { rule, score: matchRes.score };
                  }
               }
            }
            // Evaluate custom Block List
            for (let rule of this.blockList) {
               const matchRes = this.evaluateMatch(parsed.host, parsed.path, rule, parsed.isIp);
               if (matchRes.matched) {
                  if (matchRes.score > maxBlockSpecificity) {
                     maxBlockSpecificity = matchRes.score;
                     matchedCustomBlockRule = { rule, score: matchRes.score };
                  }
               }
            }
         }
         
         let customBlockInterceptionMsg = '';
         
         if (!isWhitelisted && (maxAllowSpecificity >= 0 || maxBlockSpecificity >= 0)) {
            if (maxBlockSpecificity > maxAllowSpecificity) {
               // Block wins!
               customBlockInterceptionMsg = `Specificity Conflict: Custom Block List matched "${matchedCustomBlockRule.rule}" (Score: ${matchedCustomBlockRule.score}), which is more specific than Allow List matched "${matchedCustomAllowRule ? matchedCustomAllowRule.rule : 'None'}" (Score: ${maxAllowSpecificity >= 0 ? matchedCustomAllowRule.score : -1}). Access Blocked.`;
               pipeline.push({
                  name: 'Specificity Matching Engine',
                  status: 'BLOCKED',
                  message: customBlockInterceptionMsg
               });
               finalVerdict = { result: 'Blocked', reason: customBlockInterceptionMsg, code: 315, blockType: 'Block List Match' };
               isTerminated = true;
            } else {
               // Allow wins or equals!
               isWhitelisted = true;
               matchedAllowRule = `Custom Allow (${matchedCustomAllowRule.rule})`;
               pipeline.push({
                  name: 'Specificity Matching Engine',
                  status: 'EVALUATED_PASS_BYPASS',
                  message: `Specificity Conflict Resolved: Custom Allow List matched "${matchedCustomAllowRule.rule}" (Score: ${matchedCustomAllowRule.score}) which takes precedence over Block List matched "${matchedCustomBlockRule ? matchedCustomBlockRule.rule : 'None'}" (Score: ${maxBlockSpecificity >= 0 ? matchedCustomBlockRule.score : -1}). Bypass activated.`
               });
            }
         } else {
            pipeline.push({
               name: 'Specificity Matching Engine',
               status: isTerminated ? 'SKIPPED' : (isWhitelisted ? 'BYPASSED' : 'EVALUATED_SKIP'),
               message: isWhitelisted ? 'Bypassed by higher-priority Whitelist rule.' : 'No overlapping Custom Allow/Block rules matched. Proceeding to safety heuristics.'
            });
         }
         
         // Helper to add standard steps
         const addStep = (name, blockCond, code, blockType, failMsg, passMsg) => {
            if (isTerminated) {
               pipeline.push({ name, status: 'SKIPPED', message: 'Evaluation bypassed or already terminated.' });
               return;
            }
            if (isWhitelisted) {
               pipeline.push({ name, status: 'BYPASSED', message: `Bypassed by matched whitelist/bypass rule ("${matchedAllowRule}").` });
               return;
            }
            
            if (blockCond) {
               pipeline.push({ name, status: 'BLOCKED', message: failMsg });
               finalVerdict = { result: 'Blocked', reason: failMsg, code, blockType };
               isTerminated = true;
            } else {
               pipeline.push({ name, status: 'EVALUATED_PASS', message: passMsg });
            }
         };
         
         // ── STEP 5: Phishing Protection Block (Scan-based) ──
         const isPhishingBlock = this.settings.isPhishingProtectionEnabled && this.settings.pageHasPassword && !isWhitelisted;
         addStep(
            'Phishing Protection Block',
            isPhishingBlock,
            352,
            'Phishing Scan Block',
            'Phishing Protection Intercept: Entering passwords on non-whitelisted/non-system pages is blocked to secure corporate credentials.',
            'Phishing Protection scanned. No secure password fields detected on un-whitelisted page, or protection is off.'
         );
         
         // ── STEP 6: Block All Websites Mode ──
         addStep(
            'Block All Websites Mode',
            this.settings.blockAllWebsites,
            357,
            'Block All Websites',
            'All traffic is blocked except explicitly whitelisted domains because "Block All Websites" is toggled on.',
            'Global "Block All Websites" is disabled. Continuing.'
         );
         
         // ── STEP 7: Threat Intelligence Unsafe Website DB ──
         const isUnsafeThreatDb = parsed.host.includes('bad-tracker') || parsed.host.includes('malicious');
         addStep(
            'Threat Intelligence Check',
            isUnsafeThreatDb,
            356,
            'Malware & Threat DB',
            `The host "${parsed.host}" was found in the XSurfLog global threat intelligence database of active malware or phishing targets.`,
            'Passed threat database checks. Domain is not marked as known malware.'
         );
         
         // ── STEP 8: Length & Dash Heuristics ──
         const lengthViolation = parsed.host.length > 100;
         addStep(
            'Domain Safety Heuristics',
            lengthViolation,
            353,
            'Domain Length Limit',
            `Domain length of ${parsed.host.length} characters exceeds the platform safety limit (100).`,
            `Domain length (${parsed.host.length} chars) is within acceptable security bounds.`
         );
         
         // .info malware heuristic
         const infoDashViolation = parsed.host.endsWith('info') && parsed.host.includes('-');
         addStep(
            '.info Dash Heuristic',
            infoDashViolation,
            358,
            'Suspicious .info Dashes',
            'Suspicious .info TLD containing multiple hyphens (highly characteristic of automated mass malware domains).',
            'Passed suspicious .info TLD heuristics.'
         );
         
         // ── STEP 9: OAuth Security Check ──
         const isOAuthUrl = urlStr.includes('oauth') && (urlStr.includes('client_id') || urlStr.includes('redirect_uri'));
         addStep(
            'OAuth Connection Lock',
            isOAuthUrl && this.settings.blockChatMessaging, // Simulated condition under chat block
            360,
            'OAuth Lockdown',
            'SSO / OAuth authentication request locked by account corporate login policies.',
            'No locked OAuth pattern detected.'
         );
         
         // ── STEP 10: HTTP Protocol Enforcement ──
         const isLocalHost = parsed.host === 'localhost' || parsed.host === '127.0.0.1';
         const isHttpViolation = this.settings.enableBlockHttp && parsed.scheme === 'http' && !isLocalHost;
         addStep(
            'HTTP Protocol Enforcement',
            isHttpViolation,
            346,
            'Block HTTP Protocol',
            `Blocked unsecure, unencrypted "${parsed.scheme}" connection. Corporate policy forces safe HTTPS.`,
            isLocalHost ? 'Bypassed protocol block: Localhost loopback connection.' : 'Connection protocol is secure (HTTPS).'
         );
         
         // ── STEP 11: Specialized Advanced Filters ──
         // File sharing block
         const isFileSharing = parsed.host.includes('dropbox.com') || parsed.host.includes('onedrive') || parsed.host.includes('box.com');
         addStep(
            'File Sharing Protection',
            isFileSharing && this.settings.blockFileSharing,
            351,
            'File Sharing Restrictions',
            `Blocked file-sharing domain "${parsed.host}" to protect against unauthorized data exfiltration.`,
            'Not classified as file sharing or filter is disabled.'
         );
         
         // Chat/messaging block (Standard generic checks)
         const isChatMessaging = parsed.host.includes('slack.com') || parsed.host.includes('teams.microsoft');
         addStep(
            'Chat & Messaging Protection',
            isChatMessaging && this.settings.blockChatMessaging,
            354,
            'Chat & Messaging Restrictions',
            `Blocked collaboration/chat platform "${parsed.host}" under productivity/confidentiality policies.`,
            'Not classified as chat or filter is disabled.'
         );
         
         // ── STEP 12: Government TLD Enforcement (Individual and Master Toggles) ──
         let matchedTldObj = null;
         for (let tldItem of this.governmentTlds) {
            if (parsed.host.endsWith(tldItem.Tld)) {
               matchedTldObj = tldItem;
               break;
            }
         }
         
         let govTldBlocked = false;
         let govTldAllowedBypass = false;
         let govReason = '';
         
         if (matchedTldObj) {
            if (this.settings.governmentTldSetting === 2) {
               govTldBlocked = true;
               govReason = `Government TLD matched. Global setting is set to BLOCK all Government TLDs (${parsed.host}).`;
            } else if (this.settings.governmentTldSetting === 1) {
               govTldAllowedBypass = true;
               govReason = `Government TLD matched. Global setting is set to ALLOW all Government TLDs (${parsed.host}).`;
            } else {
               // Account-specific (Individual settings)
               const status = matchedTldObj.Status;
               if (status === 2) {
                  govTldBlocked = true;
                  govReason = `Government TLD control: "${matchedTldObj.Name}" (${matchedTldObj.Tld}) is individually set to BLOCK.`;
               } else if (status === 1) {
                  govTldAllowedBypass = true;
                  govReason = `Government TLD control: "${matchedTldObj.Name}" (${matchedTldObj.Tld}) is individually set to ALLOW. Subsequent blocks bypassed.`;
               } else {
                  govReason = `Government TLD control: "${matchedTldObj.Name}" (${matchedTldObj.Tld}) is set to OFF/Default. Continuing to AI and category filters.`;
               }
            }
         }
         
         if (matchedTldObj) {
            if (govTldBlocked) {
               addStep(
                  'Government TLD Evaluation',
                  true,
                  361,
                  'Government TLD Restriction',
                  govReason,
                  ''
               );
            } else if (govTldAllowedBypass) {
               isWhitelisted = true;
               matchedAllowRule = `Gov TLD Allowed (${matchedTldObj.Name})`;
               pipeline.push({
                  name: 'Government TLD Evaluation',
                  status: 'EVALUATED_PASS_BYPASS',
                  message: govReason
               });
            } else {
               pipeline.push({
                  name: 'Government TLD Evaluation',
                  status: isTerminated ? 'SKIPPED' : 'EVALUATED_PASS',
                  message: govReason
               });
            }
         } else {
            pipeline.push({
               name: 'Government TLD Evaluation',
               status: isTerminated ? 'SKIPPED' : (isWhitelisted ? 'BYPASSED' : 'EVALUATED_PASS'),
               message: 'Domain does not belong to any government TLD registries.'
            });
         }
         
         // ── STEP 13: AI Categories Enforcement (Individual and Master Toggles) ──
         let matchedAiCategoryObj = null;
         for (let aiCat of this.aiCategories) {
            if (parsed.host.endsWith(aiCat.Domain)) {
               matchedAiCategoryObj = aiCat;
               break;
            }
         }
         
         let aiCategoryBlocked = false;
         let aiCategoryAllowedBypass = false;
         let aiReason = '';
         
         if (matchedAiCategoryObj) {
            if (this.settings.aiCategorySetting === 2) {
               // Master Block
               aiCategoryBlocked = true;
               aiReason = `AI platform domain matched. Global setting is set to BLOCK all AI Categories (${parsed.host}).`;
            } else if (this.settings.aiCategorySetting === 1) {
               // Master Allow
               aiCategoryAllowedBypass = true;
               aiReason = `AI platform domain matched. Global setting is set to ALLOW all AI Categories (${parsed.host}).`;
            } else {
               // Account-specific (Individual settings)
               const status = matchedAiCategoryObj.Status;
               if (status === 2) {
                  aiCategoryBlocked = true;
                  aiReason = `AI platform control: "${matchedAiCategoryObj.Name}" is individually set to BLOCK.`;
               } else if (status === 1) {
                  aiCategoryAllowedBypass = true;
                  aiReason = `AI platform control: "${matchedAiCategoryObj.Name}" is individually set to ALLOW. Subsequent blocks bypassed.`;
               } else {
                  aiReason = `AI platform control: "${matchedAiCategoryObj.Name}" is set to OFF/Default. Continuing to standard filters.`;
               }
            }
         }
         
         if (matchedAiCategoryObj) {
            if (aiCategoryBlocked) {
               addStep(
                  'AI Categories Evaluation',
                  true,
                  362,
                  'AI Category Restriction',
                  aiReason,
                  ''
               );
            } else if (aiCategoryAllowedBypass) {
               isWhitelisted = true;
               matchedAllowRule = `AI Allowed (${matchedAiCategoryObj.Name})`;
               pipeline.push({
                  name: 'AI Categories Evaluation',
                  status: 'EVALUATED_PASS_BYPASS',
                  message: aiReason
               });
            } else {
               pipeline.push({
                  name: 'AI Categories Evaluation',
                  status: isTerminated ? 'SKIPPED' : 'EVALUATED_PASS',
                  message: aiReason
               });
            }
         } else {
            pipeline.push({
               name: 'AI Categories Evaluation',
               status: isTerminated ? 'SKIPPED' : (isWhitelisted ? 'BYPASSED' : 'EVALUATED_PASS'),
               message: 'Domain does not belong to any registered AI platforms.'
            });
         }
         
         // ── STEP 14: Content Categories Check ──
         let matchedCategory = '';
         let categoryBlocked = false;
         if (!isTerminated && !isWhitelisted) {
            // Predict category based on keywords
            if (parsed.host.includes('facebook') || parsed.host.includes('twitter') || parsed.host.includes('instagram')) {
               matchedCategory = 'Social Media';
            } else if (parsed.host.includes('casino') || parsed.host.includes('poker') || parsed.host.includes('bet')) {
               matchedCategory = 'Gambling';
            } else if (parsed.host.includes('porn') || parsed.host.includes('xxx')) {
               matchedCategory = 'Adult Content';
            } else if (parsed.host.includes('cnn') || parsed.host.includes('bbc') || parsed.host.includes('news')) {
               matchedCategory = 'News & Media';
            } else if (parsed.host.includes('amazon') || parsed.host.includes('ebay') || parsed.host.includes('shop')) {
               matchedCategory = 'Shopping';
            } else {
               matchedCategory = 'Business & Corporate';
            }
            
            categoryBlocked = this.categories[matchedCategory] === true;
         }
         
         addStep(
            'Category Block Rules',
            categoryBlocked,
            349,
            'WebCategory Restriction',
            `Classified this URL under the category "${matchedCategory}", which is set to BLOCKED in your Categories setup.`,
            matchedCategory ? `Classified as "${matchedCategory}". Category is ALLOWED.` : 'Uncategorized or category is safe.'
         );
         
         this.pipeline = pipeline;
         this.verdict = finalVerdict;
      },
      
      // Styling Helpers
      stepClass(status) {
         if (status === 'BLOCKED') return 'timeline-blocked';
         if (status === 'EVALUATED_PASS' || status === 'EVALUATED_PASS_BYPASS') return 'timeline-pass';
         if (status === 'BYPASSED') return 'timeline-bypassed';
         return 'timeline-skipped';
      },
      
      stepIcon(status) {
         if (status === 'BLOCKED') return 'fa-solid fa-circle-xmark text-danger';
         if (status === 'EVALUATED_PASS' || status === 'EVALUATED_PASS_BYPASS') return 'fa-solid fa-circle-check text-success';
         if (status === 'BYPASSED') return 'fa-solid fa-circle-minus text-muted';
         return 'fa-regular fa-circle text-muted';
      },
      
      statusLabel(status) {
         if (status === 'BLOCKED') return 'Blocked';
         if (status === 'EVALUATED_PASS') return 'Pass';
         if (status === 'EVALUATED_PASS_BYPASS') return 'Bypass';
         if (status === 'BYPASSED') return 'Bypassed';
         return 'Skipped';
      },
      
      statusBadgeClass(status) {
         if (status === 'BLOCKED') return 'bg-danger bg-opacity-10 text-danger';
         if (status === 'EVALUATED_PASS') return 'bg-success bg-opacity-10 text-success';
         if (status === 'EVALUATED_PASS_BYPASS') return 'bg-info bg-opacity-10 text-info';
         if (status === 'BYPASSED') return 'bg-secondary bg-opacity-10 text-muted';
         return 'bg-light text-muted';
      }
   }
}
</script>

<style scoped>
.custom-span-lg { font-size: 24px; line-height: 24px; font-weight: bold; }
.custom-span-no-weight { font-size: 24px; line-height: 24px; font-weight: 250; }
.text-dark-blue { color: #354a5f; }
.text-cyan { color: #00A0C1; }
.bg-light-gray { background-color: #f8fafc; }
.font-weight-500 { font-weight: 500; }
.font-weight-600 { font-weight: 600; }
.font-weight-700 { font-weight: 700; }
.tracking-wider { letter-spacing: 0.05em; }
.line-height-1.4 { line-height: 1.4; }
.line-height-1.6 { line-height: 1.6; }
.line-height-1.7 { line-height: 1.7; }

/* Custom pills */
.custom-tabs .nav-link {
   color: #64748b;
   border: none;
   background: transparent;
   transition: all 0.25s ease;
}
.custom-tabs .nav-link.active {
   background-color: #ffffff;
   color: #00A0C1;
   box-shadow: 0 4px 10px rgba(0, 160, 193, 0.08);
}

/* Custom Switch Cards */
.custom-switch-card {
   background: #f8f9fa;
   border: 1px solid #eef1f3;
   border-radius: 8px;
   padding: 12px 16px 12px 42px;
   transition: all 0.2s ease;
   margin-left: 0 !important;
}
.custom-switch-card:hover {
   background: #ffffff;
   border-color: #00A0C1;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.06);
}
.custom-switch-card input {
   float: left;
   margin-left: -32px;
}

/* Preset buttons */
.btn-preset {
   font-size: 11px;
   padding: 4px 10px;
   border: 1px solid #e2e8f0;
   border-radius: 20px;
   background: #fff;
   color: #4a5568;
   transition: all 0.2s ease;
}
.btn-preset:hover {
   background: #00A0C1;
   color: #fff;
   border-color: #00A0C1;
}

/* Accordion adjustments */
.rule-accordion-item {
   border: 1px solid #eef1f3;
   border-radius: 10px !important;
   margin-bottom: 10px;
   overflow: hidden;
}
.rule-accordion-item .accordion-button {
   background-color: #fff;
   color: #435666;
   font-weight: 600;
   font-size: 14px;
   padding: 14px 20px;
   box-shadow: none;
}
.rule-accordion-item .accordion-button:not(.collapsed) {
   background-color: #f8fafc;
   color: #00A0C1;
   border-bottom: 1px solid #eef1f3;
}

/* Chip/Badge styling */
.badge-rule {
   font-size: 12px;
   font-weight: 500;
   padding: 6px 12px;
   border-radius: 20px;
}
.badge-rule i {
   font-size: 11px;
   cursor: pointer;
}

/* Verdict Banners */
.verdict-banner {
   transition: background-color 0.3s ease;
}
.verdict-banner.bg-success {
   background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}
.verdict-banner.bg-danger {
   background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}
.verdict-large-icon {
   font-size: 42px;
   filter: drop-shadow(0 2px 4px rgba(0,0,0,0.15));
}

/* Timeline */
.timeline {
   position: relative;
   padding-left: 20px;
   margin-top: 10px;
}
.timeline::before {
   content: '';
   position: absolute;
   left: 6px;
   top: 10px;
   bottom: 10px;
   width: 2px;
   background-color: #e2e8f0;
}

.timeline-item {
   position: relative;
   margin-bottom: 5px;
}
.timeline-marker {
   position: absolute;
   left: -20px;
   top: 1px;
   background: #fff;
   z-index: 2;
   font-size: 14px;
}

.timeline-item.timeline-pass .timeline-content {
   border-left: 3px solid #10b981;
   padding-left: 12px;
}
.timeline-item.timeline-blocked .timeline-content {
   border-left: 3px solid #ef4444;
   padding-left: 12px;
   background-color: rgba(239, 68, 68, 0.03);
   border-radius: 0 8px 8px 0;
}
.timeline-item.timeline-bypassed .timeline-content {
   border-left: 3px solid #00A0C1;
   padding-left: 12px;
   opacity: 0.8;
}
.timeline-item.timeline-skipped .timeline-content {
   border-left: 3px solid #cbd5e1;
   padding-left: 12px;
   opacity: 0.6;
}

.timeline-title {
   letter-spacing: 0.01em;
}

/* Written Guide Step explanation cards */
.explain-num {
   width: 28px;
   height: 28px;
   background-color: #00A0C1;
   color: #ffffff;
   font-size: 13px;
   font-weight: 700;
   border-radius: 50%;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}

/* Button xs */
.btn-xs {
   font-size: 10.5px;
   padding: 2px 6px;
}

.shadow-xs {
   box-shadow: 0 1px 3px rgba(0,0,0,0.03);
}

/* Custom Scroll */
.scroll-container::-webkit-scrollbar {
   width: 6px;
}
.scroll-container::-webkit-scrollbar-track {
   background: #f1f1f1;
}
.scroll-container::-webkit-scrollbar-thumb {
   background: #cbd5e1;
   border-radius: 4px;
}
.scroll-container::-webkit-scrollbar-thumb:hover {
   background: #94a3b8;
}

/* Layout inputs */
.search-group {
   background: #fff;
   border-radius: 10px;
   overflow: hidden;
}
.search-group .input-group-text {
   background-color: #f8fafc;
   border-color: #e2e8f0;
}
.search-group input {
   border-color: #e2e8f0;
}
.search-group button {
   border-color: #e2e8f0;
   background: #f8fafc;
}
.search-group button:hover {
   background-color: #f1f5f9;
   color: #ef4444;
}

.hand { cursor: pointer; }
</style>
